import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Bar } from "react-chartjs-2";
import "chartjs-plugin-datalabels";

import { flatten } from "../../../../helpers";
import { formatDate } from "../../../../helpers/date";

import CircularChart from "../../../user/CircularChart";
import "./Employee.scss";

const chartOptions = {
  plugins: {
    title: {
      display: false
    },
    datalabels: {
      color: "white"
    }
  },
  responsive: true,
  scales: {
    x: {
      stacked: true
    },
    y: {
      stacked: true
    }
  }
};

const Employee = ({ employee }) => {
  const assignedTo = employee.assigned_to[0] || {};
  const initials = assignedTo.full_name
    ? assignedTo.full_name.split(" ").map((n) => n.charAt(0))
    : [];

  const monthsData = useMemo(
    () =>
      flatten(
        employee.calls.map((calls) =>
          Object.keys(calls).map((month) => ({
            ...calls[month].reduce(
              (acc, curr) => ({
                actual_hrs: acc.actual_hrs + curr.actual_hrs,
                billable_hrs: acc.billable_hrs + curr.billable_hrs
              }),
              { actual_hrs: 0, billable_hrs: 0 }
            ),
            month: formatDate(calls[month][0].completed_date, "MMM yyyy")
          }))
        )
      ),
    [employee.calls]
  );

  const chartData = useMemo(
    () => ({
      labels: monthsData.map((month) => month.month),
      datasets: [
        {
          label: "Hours Worked",
          data: monthsData.map((month) => month.actual_hrs),
          backgroundColor: "#50ACCB"
        },
        {
          label: "Billable",
          data: monthsData.map((month) => month.billable_hrs),
          backgroundColor: "#428DA7"
        }
      ]
    }),
    [monthsData]
  );

  return (
    <div className="content-wrapper employee-wrapper">
      <div className="summary-info-wrapper">
        <div className="profile-picture">
          {assignedTo.avatar ? (
            <img src={assignedTo.avatar} alt="avatar" size="lg" />
          ) : (
            <div className="user-icon">
              <span>{initials.join("")}</span>
            </div>
          )}
          <CircularChart
            className="circular-chart animate"
            percentage={(employee.billable_hrs / employee.actual_hrs) * 100}
            size={150}
          >
            <svg role="img" xmlns="http://www.w3.org/2000/svg">
              <circle className="circle-background" />
              <circle className="circle-foreground" />
            </svg>
          </CircularChart>
        </div>
        <div className="employee-info">
          <h2>{assignedTo.full_name}</h2>
          <table className="employee-summary-table">
            <thead>
              <tr>
                <th>Hours worked</th>
                <th>{employee.actual_hrs}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Billable</td>
                <td>{employee.billable_hrs}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="employee-summary-chart-container">
        <Bar options={chartOptions} data={chartData} />
      </div>
    </div>
  );
};

Employee.propTypes = {
  employee: PropTypes.object
};

Employee.defaultProps = {
  employee: {}
};

export default Employee;
